import { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { XMarkIcon, Bars3BottomRightIcon, XCircleIcon } from '@heroicons/react/24/solid';
import rrwebPlayer from 'rrweb-player';
import type { eventWithTime } from '@rrweb/types';
import rumService from '../../services/api/rum.service';
import 'rrweb-player/dist/style.css';

interface SessionReplayModalProps {
  sessionId: string;
  onClose: () => void;
}

interface SessionInfo {
  id: string;
  session_id: string;
  user_id?: string;
  platform: string;
  device_type?: string;
  browser_name?: string;
  os_name?: string;
  country?: string;
  started_at: string;
  duration_ms?: number;
}

interface TimelineEvent {
  timestamp: number;
  type: string;
  title: string;
  details?: any;
}

interface PageSegment {
  pageNumber: number;
  startIndex: number;
  endIndex: number;
  url?: string;
  eventCount: number;
}

// CRITICAL: Keep track of loaded sessions OUTSIDE component to survive re-renders
const loadedSessions = new Set<string>();

export default function SessionReplayModal({ sessionId, onClose }: SessionReplayModalProps) {
  console.log('🔵 [SessionReplayModal] COMPONENT MOUNTED/RE-RENDERED - sessionId:', sessionId);

  const { t } = useTranslation(['rum', 'common']);

  const [session, setSession] = useState<SessionInfo | null>(null);
  const [timeline, setTimeline] = useState<TimelineEvent[]>([]);
  const [allEvents, setAllEvents] = useState<any[]>([]);
  const [pages, setPages] = useState<PageSegment[]>([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingProgress, setLoadingProgress] = useState<string>(t('rum:sessionReplay.loading.initializing'));
  const [error, setError] = useState<string | null>(null);

  const playerContainerRef = useRef<HTMLDivElement>(null);
  const playerRef = useRef<rrwebPlayer | null>(null);
  const clickSoundRef = useRef<HTMLAudioElement | null>(null);

  // Mobile timeline sidebar toggle
  const [showTimelineMobile, setShowTimelineMobile] = useState(false);

  // Initialize click sound using Web Audio API
  useEffect(() => {
    const initClickSound = () => {
      try {
        // Create AudioContext
        const AudioContext = window.AudioContext || (window as any).webkitAudioContext;
        const audioContext = new AudioContext();

        // Function to play click sound - more pronounced
        const playClickSound = () => {
          // Resume AudioContext if suspended (browser autoplay policy)
          if (audioContext.state === 'suspended') {
            audioContext.resume();
          }

          const oscillator = audioContext.createOscillator();
          const gainNode = audioContext.createGain();

          oscillator.connect(gainNode);
          gainNode.connect(audioContext.destination);

          // Create a more noticeable "click" sound
          oscillator.frequency.value = 1000; // Higher pitch
          oscillator.type = 'square'; // Square wave for sharper sound

          // Louder with quick attack and decay
          gainNode.gain.setValueAtTime(0.5, audioContext.currentTime); // Louder
          gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.08);

          oscillator.start(audioContext.currentTime);
          oscillator.stop(audioContext.currentTime + 0.08);
        };

        // Store the play function
        (clickSoundRef as any).current = { play: playClickSound, audioContext };
      } catch (e) {
        console.error('[SessionReplay] Failed to initialize click sound:', e);
      }
    };
    initClickSound();

    // Cleanup
    return () => {
      if ((clickSoundRef as any).current?.audioContext) {
        (clickSoundRef as any).current.audioContext.close();
      }
    };
  }, []);

  // Load session data and recordings
  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      if (cancelled) return;

      // CRITICAL: Only load data ONCE to prevent reset during playback
      const isAlreadyLoaded = loadedSessions.has(sessionId);
      console.log('[SessionReplay] useEffect load() called - isAlreadyLoaded:', isAlreadyLoaded);

      if (isAlreadyLoaded) {
        console.warn('[SessionReplay] ⚠️⚠️⚠️ Data already loaded or loading, BLOCKING reload to prevent page reset');
        return;
      }

      // CRITICAL: Mark as loading IMMEDIATELY (before async call) to prevent duplicate loads during re-renders
      loadedSessions.add(sessionId);
      console.log('[SessionReplay] ✓ Marked session as loading BEFORE async call');

      try {
        console.log('[SessionReplay] Loading session data for first time...');
        await loadSessionData();
        console.log('[SessionReplay] ✓ Data loaded successfully');
      } catch (err) {
        if (!cancelled) {
          console.error('[SessionReplay] Error loading session data:', err);
          // Remove from set on error so user can retry
          loadedSessions.delete(sessionId);
        }
      }
    };

    console.log('[SessionReplay] useEffect triggered for session:', sessionId, '- isLoaded:', loadedSessions.has(sessionId));
    load();

    return () => {
      console.log('🟡 [SessionReplay] CLEANUP - cancelling async load for session:', sessionId);
      cancelled = true;
      // DON'T remove from loadedSessions here - component unmounts/remounts during re-renders
      // Only clear when modal actually closes (in main cleanup useEffect)
    };
  }, [sessionId]);

  // Handle ESC key
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [onClose]);

  // Prevent body scroll and suppress CORS errors
  useEffect(() => {
    document.body.style.overflow = 'hidden';

    // Suppress CORS font/resource errors
    const originalConsoleError = console.error;
    const originalConsoleWarn = console.warn;

    console.error = (...args: any[]) => {
      const msg = args.join(' ');
      if (msg.includes('CORS') || msg.includes('Access-Control-Allow-Origin') ||
          msg.includes('ElegantIcons') || msg.includes('ERR_FAILED') ||
          msg.includes('replayer has been destroyed')) {
        return; // Silently ignore
      }
      originalConsoleError.apply(console, args);
    };

    console.warn = (...args: any[]) => {
      const msg = args.join(' ');
      if (msg.includes('CORS') || msg.includes('Access-Control-Allow-Origin') ||
          msg.includes('ElegantIcons') || msg.includes('replayer has been destroyed')) {
        return; // Silently ignore
      }
      originalConsoleWarn.apply(console, args);
    };

    return () => {
      document.body.style.overflow = 'auto';
      console.error = originalConsoleError;
      console.warn = originalConsoleWarn;
    };
  }, []);

  // Cleanup on unmount - remove session from loaded set to allow reopening
  useEffect(() => {
    return () => {
      if (playerRef.current) {
        playerRef.current.pause();
        playerRef.current = null;
      }
      // Clear session from loaded set to allow reopening
      console.log('[SessionReplay] Cleanup - removing session from loadedSessions:', sessionId);
      loadedSessions.delete(sessionId);
    };
  }, [sessionId]);

  // Monitor currentPage changes (debug)
  useEffect(() => {
    console.log(`🔴 [State] currentPage changed to: ${currentPage} (Page ${pages[currentPage]?.pageNumber || 'N/A'})`);
    console.trace('🔴 Stack trace for currentPage change:');
  }, [currentPage]);

  // Helper function to extract URL from FullSnapshot event
  function extractUrlFromEvent(event: any): string | undefined {
    if (event && event.type === 2) { // FullSnapshot
      return event.data?.href || event.data?.node?.href || undefined;
    }
    return undefined;
  }

  // Initialize rrweb player with given events
  async function initializePlayer(events: any[], pageSegment?: PageSegment) {
    if (!playerContainerRef.current) {
      console.error('[SessionReplay] Player container not found!');
      return;
    }

    // Validate events count (rrweb requires at least 2 events)
    if (!events || events.length < 2) {
      console.error('[SessionReplay] Not enough events to initialize player:', events?.length);
      setError(`Not enough events to play recording (need at least 2, got ${events?.length || 0})`);
      setLoading(false);
      return;
    }

    // Destroy previous player completely before creating new one
    if (playerRef.current) {
      console.log('[SessionReplay] Destroying previous player...');
      try {
        playerRef.current.pause();
        // Wait a bit for player to fully stop
        await new Promise(resolve => setTimeout(resolve, 50));
      } catch (e) {
        console.warn('[SessionReplay] Error pausing previous player:', e);
      }
      playerRef.current = null;
    }

    // Clear previous player container
    playerContainerRef.current.innerHTML = '';

    console.log('[SessionReplay] Initializing NEW independent player for page', pageSegment?.pageNumber || 1);
    setLoadingProgress(t('rum:sessionReplay.loading.buildingTimeline'));

    // Extract timeline events from rrweb events for sidebar and create tags for player
    const timelineEvents: TimelineEvent[] = [];
    const playerTags: Record<number, string> = {};
    let startTime = events[0]?.timestamp || 0;

    console.log('[SessionReplay] Page start time:', startTime);

    // Process all events at once - it's fast enough
    events.forEach((event: any, index: number) => {
      if (index < 3) {
        console.log(`[SessionReplay] Processing event ${index}:`, event);
      }
      const relativeTime = event.timestamp - startTime;

      // Map rrweb event types to our timeline
      let eventType = 'unknown';
      let eventTitle = 'Unknown Event';

      switch (event.type) {
        case 2: // Full snapshot
          eventType = 'snapshot';
          eventTitle = 'Page Snapshot';
          break;
        case 3: // Incremental snapshot
          if (event.data?.source === 2) {
            // Mouse interaction
            eventType = 'click';
            eventTitle = 'Click';
          } else if (event.data?.source === 3) {
            // Scroll
            eventType = 'scroll';
            eventTitle = 'Scroll';
          } else if (event.data?.source === 5) {
            // Input
            eventType = 'input';
            eventTitle = 'Input';
          }
          break;
        case 4: // Meta
          eventType = 'meta';
          eventTitle = 'Page Info';
          break;
        case 5: // Custom event
          eventType = 'custom';
          eventTitle = event.data?.tag || 'Custom Event';
          break;
      }

      if (eventType !== 'unknown' && eventType !== 'meta') {
        timelineEvents.push({
          timestamp: relativeTime, // Use relative time for player navigation
          type: eventType,
          title: eventTitle,
          details: event.data,
        });

        // Add tag for player timeline (relative time)
        playerTags[relativeTime] = eventTitle;
      }
    });

    // Update timeline once with all events
    console.log('[SessionReplay] Timeline events built:', timelineEvents.length);
    console.log('[SessionReplay] First 3 timeline events:', timelineEvents.slice(0, 3));
    setTimeline(timelineEvents);

    console.log('[SessionReplay] Timeline state updated, initializing player...');
    setLoadingProgress(t('rum:sessionReplay.loading.initializingPlayer'));

    // Create new player with tags
    const containerHeight = playerContainerRef.current.offsetHeight;
    const containerWidth = playerContainerRef.current.offsetWidth;

    // Calculate proper dimensions leaving space for controls
    const playerWidth = Math.max(containerWidth - 0, 800);
    const playerHeight = Math.max(containerHeight - 80, 500);

    // Track click events with their timestamps
    const clickTimestamps = new Set<number>();
    const recordingStartTime = events[0]?.timestamp || 0;

    // Extract click event timestamps
    events.forEach((event: any) => {
      if (event.type === 3 && event.data?.source === 2) { // IncrementalSnapshot with MouseInteraction
        // MouseInteraction type 2 = Click, 3 = DblClick
        if (event.data.type === 2 || event.data.type === 3) {
          clickTimestamps.add(event.timestamp); // Store absolute timestamp
        }
      }
    });

    setLoadingProgress(t('rum:sessionReplay.loading.renderingPlayer'));

    // Create player asynchronously with error handling
    try {
      playerRef.current = new rrwebPlayer({
        target: playerContainerRef.current,
        props: {
          events: events as eventWithTime[],
          width: playerWidth,
          height: playerHeight,
          autoPlay: true, // Don't auto-play to prevent restarts
          speedOption: [1, 2, 4, 8],
          showController: true,
          showWarning: false, // Disable warnings to avoid CORS font errors
          skipInactive: true,
          tags: playerTags,
          UNSAFE_replayCanvas: false, // Prevent canvas issues
          triggerFocus: false, // Don't steal focus
          // Ignore stylesheet errors (CORS, missing fonts, etc)
          unpackFn: (remote: any) => {
            // Silently catch and ignore stylesheet/font loading errors
            return remote;
          },
          // Enable mouse interaction feedback
          mouseTail: {
            duration: 500,
            lineCap: 'round',
            lineWidth: 2,
            strokeStyle: 'red'
          },
          plugins: [
            // Custom plugin to play click sounds for each page independently
            {
              handler: (event: any, isSync: boolean, { replayer }: any) => {
                // Each page has its own player instance, so no need to detect restarts
                // Just play sounds for click events
                if (event.type === 3 && event.data?.source === 2) {
                  // MouseInteraction type 2 = Click, 3 = DblClick
                  if (event.data.type === 2 || event.data.type === 3) {
                    // Play click sound
                    if ((clickSoundRef as any).current?.play) {
                      try {
                        (clickSoundRef as any).current.play();
                      } catch (e) {
                        // Silently ignore audio errors
                      }
                    }
                  }
                }
              },
            },
          ],
        },
      });

      // Hide loading immediately after player is created
      console.log(`[SessionReplay] ✓ Player created successfully for Page ${pageSegment?.pageNumber || 1} with ${events.length} events`);
      console.log('[SessionReplay] This is a NEW independent player instance - no connection to previous pages');
      setLoading(false);
    } catch (playerError: any) {
      console.error('[SessionReplay] Error creating rrweb player:', playerError);
      setError(`Failed to initialize player: ${playerError.message || 'Unknown error'}`);
      setLoading(false);
    }
  }

  // Handle page change
  async function handlePageChange(pageIndex: number) {
    console.log(`[handlePageChange] Called with pageIndex=${pageIndex}, currentPage=${currentPage}`);

    if (pageIndex === currentPage) {
      console.log('[handlePageChange] Same page, ignoring');
      return;
    }

    if (!allEvents.length) {
      console.warn('[handlePageChange] No events loaded, ignoring');
      return;
    }

    if (!pages[pageIndex]) {
      console.error('[handlePageChange] Invalid page index:', pageIndex, 'Available pages:', pages.length);
      return;
    }

    console.log('='.repeat(80));
    console.log(`[SessionReplay] 🔄 SWITCHING PAGE: ${pages[currentPage]?.pageNumber} → ${pages[pageIndex]?.pageNumber}`);
    console.log(`[SessionReplay] Destroying old player and creating NEW independent player`);
    console.log('='.repeat(80));

    setLoading(true);
    setLoadingProgress(`Loading Page ${pageIndex + 1}...`);

    try {
      // Get events for selected page
      const selectedPage = pages[pageIndex];
      const pageEvents = allEvents.slice(
        selectedPage.startIndex,
        selectedPage.endIndex + 1
      );

      console.log(`[SessionReplay] Loading Page ${selectedPage.pageNumber}:`, {
        startIndex: selectedPage.startIndex,
        endIndex: selectedPage.endIndex,
        eventCount: pageEvents.length,
        url: selectedPage.url || 'unknown'
      });

      // Wait a bit for UI to update
      await new Promise(resolve => setTimeout(resolve, 100));

      // Initialize player with page events
      await initializePlayer(pageEvents, selectedPage);

      // Update current page
      console.log(`[handlePageChange] ✓ Successfully switched to page ${pageIndex} (Page ${selectedPage.pageNumber})`);
      setCurrentPage(pageIndex);
    } catch (err) {
      console.error('[SessionReplay] Error switching page:', err);
      setError('Failed to load page');
      setLoading(false);
    }
  }

  async function loadSessionData() {
    try {
      setLoading(true);
      setError(null);
      setLoadingProgress(t('rum:sessionReplay.loading.loadingSession'));

      // Load session info
      const sessionData = await rumService.getSession(sessionId);
      setSession(sessionData);

      // Mark session as viewed automatically when opening replay
      try {
        await rumService.toggleSessionViewed(sessionId, true);
        console.log('[SessionReplay] Session marked as viewed');
      } catch (err) {
        console.warn('[SessionReplay] Failed to mark session as viewed:', err);
      }

      setLoadingProgress(t('rum:sessionReplay.loading.fetchingEvents'));

      // Load recording events with progress feedback
      console.log(`[SessionReplay] Fetching recording events for session: ${sessionId}`);
      const events = await rumService.getRecordingEvents(sessionId);
      console.log(`[SessionReplay] Received ${events.length} events`);

      if (!events || events.length === 0) {
        console.error('[SessionReplay] No events received!');
        setError(t('rum:sessionReplay.error.noRecording'));
        setLoading(false);
        return;
      }

      // Validate events structure
      const validEvents = events.filter((e: any) => {
        if (!e || typeof e !== 'object') {
          console.warn('[SessionReplay] Invalid event (not an object):', e);
          return false;
        }
        if (typeof e.type !== 'number' || typeof e.timestamp !== 'number') {
          console.warn('[SessionReplay] Invalid event (missing type/timestamp):', e);
          return false;
        }
        if (e.data && typeof e.data === 'object' && e.data.node && !Array.isArray(e.data.node.childNodes)) {
          // Fix missing childNodes array (common issue)
          if (e.data.node.childNodes === undefined) {
            e.data.node.childNodes = [];
          }
        }
        return true;
      });

      if (validEvents.length === 0) {
        console.error('[SessionReplay] No valid events after filtering!');
        setError('Recording data is corrupted or malformed');
        setLoading(false);
        return;
      }

      // Deduplicate events by timestamp + type
      const seenEvents = new Set<string>();
      const finalEvents = validEvents.filter((e: any) => {
        const key = `${e.timestamp}-${e.type}`;
        if (seenEvents.has(key)) {
          return false;
        }
        seenEvents.add(key);
        return true;
      });

      console.log('[SessionReplay] Final:', finalEvents.length, 'events (removed', validEvents.length - finalEvents.length, 'duplicates)');

      // Store all events for page segmentation
      setAllEvents(finalEvents);

      // Detect page segments by finding Meta+FullSnapshot pairs
      // Each page starts with Meta (type 4) followed by FullSnapshot (type 2)
      // The first pair is the initial recording, subsequent pairs are page navigations
      const pageSegments: PageSegment[] = [];

      // Find all page start indices (Meta followed by FullSnapshot)
      const pageStartIndices: number[] = [];
      finalEvents.forEach((event: any, index: number) => {
        if (event.type === 4 && index + 1 < finalEvents.length && finalEvents[index + 1].type === 2) {
          pageStartIndices.push(index);
        }
      });

      // If no Meta+FullSnapshot pair found, check for standalone FullSnapshot
      if (pageStartIndices.length === 0) {
        const fsIndex = finalEvents.findIndex((e: any) => e.type === 2);
        if (fsIndex >= 0) pageStartIndices.push(Math.max(0, fsIndex));
      }

      // Build segments: each page runs from its start index to the next page's start index - 1
      pageStartIndices.forEach((startIdx: number, i: number) => {
        const endIdx = i + 1 < pageStartIndices.length
          ? pageStartIndices[i + 1] - 1
          : finalEvents.length - 1;
        pageSegments.push({
          pageNumber: i + 1,
          startIndex: startIdx,
          endIndex: endIdx,
          url: extractUrlFromEvent(finalEvents[startIdx]),
          eventCount: endIdx - startIdx + 1
        });
      });

      // Log all segments before filtering
      console.log('[SessionReplay] All page segments:', pageSegments.map(p => ({
        pageNum: p.pageNumber,
        start: p.startIndex,
        end: p.endIndex,
        count: p.eventCount,
        url: p.url
      })));

      // Filter out page segments with less than 2 events (rrweb requires at least 2)
      let validPageSegments = pageSegments.filter(page => page.eventCount >= 2);

      console.log('[SessionReplay] Detected', pageSegments.length, 'page segments,', validPageSegments.length, 'valid (>= 2 events)');

      // If no valid pages, use all events as single page
      if (validPageSegments.length === 0) {
        console.warn('[SessionReplay] No valid page segments found, using all events as single page');
        validPageSegments = [{
          pageNumber: 1,
          startIndex: 0,
          endIndex: finalEvents.length - 1,
          url: extractUrlFromEvent(finalEvents[0]),
          eventCount: finalEvents.length
        }];
      } else {
        // Renumber pages sequentially starting from 1 after filtering
        validPageSegments = validPageSegments.map((segment, index) => ({
          ...segment,
          pageNumber: index + 1
        }));
      }

      console.log('[SessionReplay] Final valid segments:', validPageSegments.map(p => ({
        pageNum: p.pageNumber,
        start: p.startIndex,
        end: p.endIndex,
        count: p.eventCount
      })));

      setPages(validPageSegments);

      // ONLY set to page 0 if we don't have a current page yet
      if (currentPage === 0 || pages.length === 0) {
        console.log('[SessionReplay] Setting initial page to 0');
        setCurrentPage(0);
      } else {
        console.warn('[SessionReplay] ⚠️ NOT resetting currentPage - user is on page', currentPage);
      }

      // Get events for first page only
      const firstPageEvents = finalEvents.slice(
        validPageSegments[0].startIndex,
        validPageSegments[0].endIndex + 1
      );

      console.log('[SessionReplay] Loading first page:', {
        pageNumber: validPageSegments[0].pageNumber,
        startIndex: validPageSegments[0].startIndex,
        endIndex: validPageSegments[0].endIndex,
        expectedCount: validPageSegments[0].eventCount,
        actualCount: firstPageEvents.length,
        match: validPageSegments[0].eventCount === firstPageEvents.length ? '✓' : '✗ MISMATCH'
      });
      setLoadingProgress(t('rum:sessionReplay.loading.processingEvents', { count: firstPageEvents.length }));

      // Wait for DOM to be ready
      await new Promise(resolve => setTimeout(resolve, 100));

      // Continue with player initialization using firstPageEvents instead of finalEvents
      await initializePlayer(firstPageEvents, pageSegments[0]);
    } catch (err: any) {
      console.error('Error loading session data:', err);
      setError(err.message || t('rum:sessionReplay.error.loadFailed'));
      setLoading(false);
    }
  }

  function handleJumpToEvent(timestamp: number) {
    if (playerRef.current) {
      // Use absolute timestamp directly
      playerRef.current.goto(timestamp);
    }
  }

  function formatDuration(ms: number): string {
    const seconds = Math.floor(ms / 1000);
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  }

  function getEventColor(type: string): string {
    switch (type) {
      case 'snapshot':
        return 'bg-purple-500 border-purple-600';
      case 'click':
        return 'bg-blue-500 border-blue-600';
      case 'scroll':
        return 'bg-green-500 border-green-600';
      case 'input':
        return 'bg-yellow-500 border-yellow-600';
      case 'custom':
        return 'bg-orange-500 border-orange-600';
      default:
        return 'bg-gray-500 border-gray-600';
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-90 p-4">
      {/* Modal Container */}
      <div className="relative w-full h-full max-w-7xl max-h-[90vh] bg-gray-900 rounded-lg shadow-2xl flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-800 gap-2 sm:gap-0">
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between sm:justify-start">
              <h2 className="text-lg sm:text-2xl font-bold text-white truncate">
                {t('rum:sessionReplay.title')}
              </h2>
              <button
                onClick={onClose}
                className="sm:hidden text-gray-400 hover:text-white transition-colors ml-2"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>
            {session && (
              <p className="text-xs sm:text-sm text-gray-400 mt-1 truncate">
                <span className="hidden sm:inline">Session ID: {session.session_id} • </span>
                <span className="sm:hidden">{session.session_id.substring(0, 8)}... • </span>
                {session.browser_name} • {session.os_name}
                <span className="hidden sm:inline"> • {session.country}</span>
              </p>
            )}
          </div>

          {/* Page Selector */}
          {pages.length > 1 && (
            <div className="flex items-center gap-2 sm:gap-3 sm:mr-4">
              <label className="text-xs sm:text-sm font-medium text-gray-300 whitespace-nowrap">
                {t('rum:sessionReplay.pageSelector.label')}
              </label>
              <select
                value={currentPage}
                onChange={(e) => {
                  const newPageIndex = Number(e.target.value);
                  console.log(`[SessionReplayModal] Dropdown changed: ${currentPage} → ${newPageIndex}`);
                  handlePageChange(newPageIndex);
                }}
                disabled={loading}
                className="bg-gray-800 text-white border border-gray-700 rounded-lg px-2 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed min-w-0 sm:min-w-[200px] flex-1 sm:flex-none"
              >
                {pages.map((page, index) => {
                  let urlPath = '';
                  if (page.url) {
                    try {
                      urlPath = ` - ${new URL(page.url).pathname}`;
                    } catch {
                      urlPath = ' - ' + page.url.substring(0, 30);
                    }
                  }
                  return (
                    <option key={index} value={index}>
                      {t('rum:sessionReplay.pageSelector.pageInfo', {
                        number: page.pageNumber,
                        url: urlPath,
                        count: page.eventCount
                      })}
                    </option>
                  );
                })}
              </select>
            </div>
          )}

          <button
            onClick={onClose}
            className="hidden sm:block text-gray-400 hover:text-white transition-colors"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        {/* Multi-page info banner */}
        {pages.length > 1 && (
          <div className="px-4 sm:px-6 py-2 sm:py-3 bg-blue-900/30 border-b border-blue-800/50 flex items-center gap-2 sm:gap-3">
            <svg className="w-4 h-4 sm:w-5 sm:h-5 text-blue-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="text-xs sm:text-sm text-blue-200" dangerouslySetInnerHTML={{
              __html: t('rum:sessionReplay.pageSelector.multiPageBanner', { count: pages.length })
            }} />
          </div>
        )}

        {/* Content */}
        <div className="flex-1 flex overflow-hidden min-h-0 relative">
          {/* Mobile Timeline Toggle Button */}
          <button
            onClick={() => setShowTimelineMobile(!showTimelineMobile)}
            className="lg:hidden fixed bottom-4 right-4 z-50 bg-blue-600 hover:bg-blue-700 text-white p-3 rounded-full shadow-lg transition-all"
            title={showTimelineMobile ? t('rum:sessionReplay.timeline.hide') : t('rum:sessionReplay.timeline.show')}
          >
            {showTimelineMobile ? (
              <XCircleIcon className="w-6 h-6" />
            ) : (
              <Bars3BottomRightIcon className="w-6 h-6" />
            )}
          </button>

          {/* Player Container */}
          <div className="flex-1 bg-gray-950 flex items-center justify-center p-2 sm:p-8 overflow-hidden relative w-full lg:w-[calc(100%-320px)]">
            {/* Loading overlay */}
            {loading && (
              <div className="absolute inset-0 flex items-center justify-center bg-gray-950 z-10">
                <div className="text-center">
                  <div className="inline-block animate-spin rounded-full h-16 w-16 border-b-2 border-t-2 border-blue-500 mb-6"></div>
                  <p className="text-lg font-semibold text-white mb-2">{loadingProgress}</p>
                  <p className="text-sm text-gray-500">{t('rum:sessionReplay.loading.pleaseWait')}</p>
                </div>
              </div>
            )}

            {/* Error overlay */}
            {error && (
              <div className="absolute inset-0 flex items-center justify-center bg-gray-950 z-10">
                <div className="text-center text-red-500">
                  <p className="text-lg font-semibold">{t('rum:sessionReplay.error.title')}</p>
                  <p className="mt-2">{error}</p>
                </div>
              </div>
            )}

            {/* Player container - always rendered */}
            <div
              ref={playerContainerRef}
              className="w-full h-full"
              style={{
                maxWidth: '100%',
                maxHeight: '100%',
                contain: 'layout style paint',
                visibility: loading || error ? 'hidden' : 'visible',
              }}
            />
          </div>

          {/* Events Sidebar - Hidden on mobile, toggleable overlay */}
          <div className={`
            w-80 bg-gray-900 border-l border-gray-800 flex flex-col min-h-0 flex-shrink-0 z-20
            fixed lg:static inset-y-0 right-0
            transform transition-all duration-300 ease-in-out
            ${showTimelineMobile ? 'translate-x-0 opacity-100' : 'translate-x-full opacity-0 pointer-events-none lg:translate-x-0 lg:opacity-100 lg:pointer-events-auto'}
          `}>
            <div className="px-4 py-3 border-b border-gray-800 flex-shrink-0">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-white">{t('rum:sessionReplay.timeline.title')}</h3>
                <button
                  onClick={() => setShowTimelineMobile(false)}
                  className="lg:hidden p-1 text-gray-400 hover:text-white transition-colors"
                >
                  <XMarkIcon className="w-5 h-5" />
                </button>
              </div>
              {!loading && timeline.length > 0 && (
                <div className="text-sm text-gray-400 mt-1">
                  <p>{t('rum:sessionReplay.timeline.eventsRecorded', { count: timeline.length })}</p>
                  {pages.length > 1 && (
                    <p className="text-xs text-blue-400 mt-1">
                      {t('rum:sessionReplay.pageSelector.viewingPage', {
                        current: pages[currentPage]?.pageNumber,
                        total: pages.length
                      })}
                    </p>
                  )}
                </div>
              )}
              {loading && timeline.length > 0 && (
                <p className="text-sm text-gray-400 mt-1">
                  <span className="inline-flex items-center gap-2">
                    <span className="inline-block animate-pulse">{t('common:loading')}</span>
                    <span className="text-blue-400">({timeline.length})</span>
                  </span>
                </p>
              )}
              {loading && timeline.length === 0 && (
                <p className="text-sm text-gray-400 mt-1">
                  <span className="inline-flex items-center gap-2">
                    <span className="inline-block animate-spin rounded-full h-3 w-3 border-b border-blue-500"></span>
                    <span>{t('common:loading')}</span>
                  </span>
                </p>
              )}
            </div>

            <div
              className="flex-1 overflow-y-scroll overflow-x-hidden min-h-0"
              style={{
                scrollbarWidth: 'auto',
                scrollbarColor: '#6b7280 #1f2937'
              }}
            >
              {timeline.length === 0 && loading && (
                <div className="p-4 text-center text-gray-500">
                  <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mb-2"></div>
                  <p>{t('common:loading')}</p>
                </div>
              )}
              {timeline.length === 0 && !loading && (
                <div className="p-4 text-center text-gray-500">
                  {t('rum:sessionReplay.timeline.noEvents')}
                </div>
              )}

              {timeline.map((event, index) => {
                const colorClass = getEventColor(event.type);
                const [bgColor, borderColor] = colorClass.split(' ');

                return (
                  <div
                    key={`${event.timestamp}-${index}`}
                    onClick={() => handleJumpToEvent(event.timestamp)}
                    className="px-4 py-3 border-b border-gray-800 hover:bg-gray-800 cursor-pointer transition-colors"
                  >
                    <div className="flex items-start gap-3">
                      <div className={`w-3 h-3 rounded-full ${bgColor} border-2 ${borderColor} mt-1 flex-shrink-0`} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <p className="text-sm font-medium text-white truncate">
                            {event.title}
                          </p>
                          <span className="text-xs text-gray-500 flex-shrink-0">
                            {formatDuration(event.timestamp)}
                          </span>
                        </div>
                        <p className="text-xs text-gray-400 mt-1 capitalize">
                          {event.type}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
